import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import { ResolveValueContext } from './ResolveValueContext';
import { DocumentPropertyBoolRule } from 'types/dynamic-forms/DocumentPropertyBoolRule';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';

export function resolveBoolRule(
  rule: DocumentPropertyBoolRule,
  prop: DocumentProperty, // We technically don't need "prop" here (since it can be derived from path), but this is more performant
  path: JsonPointerPath,
  context: ResolveValueContext
): boolean {
  switch (rule.__typename) {
    case 'or':
      return resolveBoolRule(rule.a, prop, path, context) || resolveBoolRule(rule.b, prop, path, context);
    case 'and':
      return resolveBoolRule(rule.a, prop, path, context) && resolveBoolRule(rule.b, prop, path, context);
    case 'documentStatusEquals':
      return context.documentStatus === rule.value;
    case 'documentStatusNotEquals':
      return context.documentStatus !== rule.value;
    case 'anotherPropEquals': {
      const referenceValue = context.getRefValue(prop, path, rule.refId);
      return deepEquals(referenceValue, rule.value);
    }
    case 'anotherPropNotEquals': {
      const referenceValue = context.getRefValue(prop, path, rule.refId);
      return !deepEquals(referenceValue, rule.value);
    }
    case 'anyItem': {
      const referenceValue = context.getRefValue(prop, path, rule.refId);
      if (!Array.isArray(referenceValue)) {
        throw new Error(
          `Expected array value for property "${JSON.stringify(prop, path)}" in a rule but got "${typeof referenceValue}".`
        );
      }
      return referenceValue.some((item) => deepEquals(item, rule.condition.value));
    }
    case 'notEmptyOptions': {
      const options = context.getOptions(path);
      return options.length > 0;
    }
    case 'selfNotEmpty': {
      const value = context.getValue(path);
      return value !== null && value !== undefined && (typeof value !== 'string' || value.trim().length > 0);
    }
    case 'hasPublishedVersionHistory':
      return context.hasPublishedVersionHistory;
    default:
      throw new Error(`Unknown rule: ${JSON.stringify(rule)}.`);
  }
}

function deepEquals(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEquals(a[key], b[key])) {
      return false;
    }
  }

  return true;
}
